import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { X } from 'lucide-react';

import { useMyReferral } from '@/lib/api/hooks';
import { track } from '@/lib/analytics';
import { referralBannerUi } from './referralBannerUi';

// sessionStorage key — clears when the tab closes / on next login, so the
// banner reappears for each fresh session as the patient requested.
const DISMISSED_KEY = 'referral_banner_dismissed';

// Intake and onboarding routes (Healthie forms, the ID step, consent) pin a
// full-width sticky Continue button where this banner sits on mobile, so a
// tap on Continue opened the share sheet instead.
const INTAKE_ROUTE = /^\/(?:app\/intake|onboarding)(?:\/|$)/;

/**
 * Fixed bottom-anchored "Give $20, Get $20" referral promo that sits to
 * the left of the support-chat launcher. Hidden on the home screen (the
 * MemberCodeCard already lives there) and on intake routes. One-shot per
 * session: once the patient closes it, it stays gone until they start a
 * new session.
 */
export function ReferralBanner() {
  const location = useLocation();
  // Referrals rolled out to 100% (gift-referrals experiment completed
  // 2026-07-13, variant-a won).
  const { data: referral } = useMyReferral(true);
  const [dismissed, setDismissed] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (sessionStorage.getItem(DISMISSED_KEY) === '1') {
      setDismissed(true);
    }
  }, []);

  const onHome =
    location.pathname === '/app' || location.pathname === '/app/';
  const onIntake = INTAKE_ROUTE.test(location.pathname);
  const isVisible = !onHome && !onIntake && !dismissed && Boolean(referral);

  // Publish visibility so other fixed bottom elements (Shop's sticky
  // "Add to Stack" CTA) can reposition around this banner reactively.
  useEffect(() => {
    referralBannerUi.setVisible(isVisible);
    return () => referralBannerUi.setVisible(false);
  }, [isVisible]);

  // Impression — once per session (the banner is one-shot per session too).
  useEffect(() => {
    if (isVisible) track('referral_card_viewed', { surface: 'banner' });
  }, [isVisible]);

  if (!isVisible || !referral) return null;

  function handleClose() {
    sessionStorage.setItem(DISMISSED_KEY, '1');
    setDismissed(true);
    track('referral_banner_dismissed');
  }

  async function handleShare() {
    // Native share sheet on mobile + capable desktops; clipboard copy elsewhere.
    if (typeof navigator !== 'undefined' && typeof navigator.share === 'function') {
      track('referral_share_clicked', { surface: 'banner', channel: 'native' });
      try {
        await navigator.share({
          title: 'System Labs',
          text: `Get $20 off your first order at System Labs with my code ${referral!.code}.`,
          url: referral!.referral_link,
        });
        track('referral_share_completed', { surface: 'banner', channel: 'native' });
      } catch {
        // Dismissed or failed — no-op.
      }
      return;
    }
    track('referral_share_clicked', { surface: 'banner', channel: 'copy' });
    try {
      await navigator.clipboard.writeText(referral!.referral_link);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 4000);
      track('referral_share_completed', { surface: 'banner', channel: 'copy' });
    } catch {
      // Clipboard blocked — no-op.
    }
  }

  return (
    <div
      className="fixed z-helpdock right-60 bottom-60 md:bottom-20 h-40 flex items-stretch bg-[#353535] text-white w-[calc(100%-7.5rem)] md:w-[32rem]"
    >
      <button
        type="button"
        aria-label="Dismiss"
        onClick={handleClose}
        className="w-40 h-40 shrink-0 flex items-center justify-center bg-[#5A5A5A] hover:bg-[#454545]"
      >
        <X size={18} strokeWidth={1.5} />
      </button>
      <div className="flex-1 flex items-center px-15 font-body text-14 leading-100 truncate">
        Give $20, Get $20
      </div>
      <button
        type="button"
        onClick={handleShare}
        className="shrink-0 px-15 font-mono text-12 uppercase text-white/60 hover:text-white/90"
      >
        {copied ? 'Copied' : 'Share Link'}
      </button>
    </div>
  );
}
